import React, { FC, useCallback, useMemo, useState } from 'react';
import { groupBy } from 'lodash';
import AddOrEditGroupTitleModal from './AddOrEditGroupTitleModal';
import { Button, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import GroupedTaskDisplay from './GroupedTaskDisplay';
import WOTaskEntityAssociateModal from './WOTaskEntityAssociateModal';
import PMTemplateTaskEntityAssociateModal from './PMTemplateTaskEntityAssociateModal';

interface IOWTaskComponent {
	existingTasks: any[];
	isAllowedToEdit: boolean;
	bulkUpdateTasks: (tasks: any[]) => any;
	bulkDeleteTasks: (tasks: any[]) => any;
	onTaskEdit: (x: any) => any;
	onTaskDelete: (x: any) => any;
	onTasksChange?: (x: any[]) => void;
	timeZoneId: any;
	notCheckable?: boolean;
	isPMTemplate?: boolean;
	workOrder?: any;
	associationDisabled?: boolean;
	addGroupDisabled?: boolean;
	hideIndividualWalkthroughLink: boolean;
}

const OWTaskComponent: FC<IOWTaskComponent> = ({
	existingTasks = [],
	isAllowedToEdit,
	bulkUpdateTasks,
	bulkDeleteTasks,
	onTaskEdit,
	onTaskDelete,
	onTasksChange,
	timeZoneId,
	notCheckable,
	isPMTemplate = false,
	workOrder = {},
	associationDisabled = false,
	addGroupDisabled = false,
	hideIndividualWalkthroughLink,
}): React.ReactElement => {
	const [groupTitleSaving, setGroupTitleSaving] = useState(false);
	const [currentTasks, setCurrentTasks] = useState<any[]>(existingTasks);
	const [groupTitleModalVisible, setGroupTitleModalVisible] = useState(false);
	const [woAssociateModalVisible, setWoAssociateModalVisible] = useState(false);
	const [pmTemplateAssociateModalVisible, setPmTemplateAssociateModalVisible] = useState(false);
	const [currentEditingTask, setCurrentEditingTask] = useState(null);

	const getNewOrderingId = (tasks) => {
		const newOrderingId =
			tasks.length > 0 ? (Math.max(...tasks.map((_) => _.orderingId)) || 0) + 1 : 0;
		return newOrderingId;
	};

	const groupedTasks = useMemo(() => {
		const grouped = groupBy(currentTasks, (task) => task.groupTitle || '');
		return Object.keys(grouped).map((key) => ({
			groupTitle: key,
			tasks: grouped[key],
		}));
	}, [currentTasks]);

	const onEditGroupTitle = () => setGroupTitleModalVisible(true);
	const onGroupTitleCancel = () => setGroupTitleModalVisible(false);

	const _hideAssociateModal = () => {
		setWoAssociateModalVisible(false);
		setPmTemplateAssociateModalVisible(false);
		setCurrentEditingTask(null);
	};

	const onShowAssociate = useCallback(
		(task) => {
			setCurrentEditingTask(task);
			isPMTemplate ? setPmTemplateAssociateModalVisible(true) : setWoAssociateModalVisible(true);
		},
		[isPMTemplate]
	);

	const changeTitleForExistingTasks = useCallback(
		(oldTitle, newTitle) => {
			setGroupTitleSaving(true);
			const affectedTasks = currentTasks.filter(
				(task) => task.groupTitle === oldTitle && !!task.id
			);
			bulkUpdateTasks(
				affectedTasks.map((task) => ({
					...task,
					groupTitle: newTitle,
				}))
			)
				.then(() => {
					onGroupTitleCancel();
					setCurrentTasks((localTasks) => {
						const newTasks = localTasks.map((task) => ({
							...task,
							...(task.groupTitle === oldTitle && { groupTitle: newTitle }),
						}));
						onTasksChange && onTasksChange(newTasks);
						return newTasks;
					});
				})
				.catch(() => message.error('Unable to update group title. Please cotact support!'))
				.finally(() => setGroupTitleSaving(false));
		},
		[bulkUpdateTasks, currentTasks, onTasksChange]
	);

	const deleteGroup = useCallback(
		(groupTitle) => {
			const affectedTasks = currentTasks.filter(
				(task) => task.groupTitle === groupTitle && !!task.id
			);
			bulkDeleteTasks(
				affectedTasks.map((task) => ({
					...task,
					isDeleted: true,
				}))
			)
				.then(() => {
					setCurrentTasks((localTasks) => {
						const newTasks = localTasks.filter((task) => task.groupTitle !== groupTitle);
						onTasksChange && onTasksChange(newTasks);
						return newTasks;
					});
				})
				.catch(() => message.error('Unable to update group title. Please cotact support!'));
		},
		[bulkDeleteTasks, currentTasks, onTasksChange]
	);

	const onAddNewTask = useCallback(
		(groupTitle) => () => {
			setCurrentTasks((localTasks) => [
				...localTasks,
				...[{ groupTitle, orderingId: getNewOrderingId(localTasks) }],
			]);
		},
		[]
	);

	const onSuccess = useCallback(
		(updatedTask) => {
			setCurrentTasks((tasks) => {
				const newTasks = tasks.map((task) =>
					updatedTask.orderingId === task.orderingId ? { ...updatedTask } : { ...task }
				);
				onTasksChange && onTasksChange(newTasks);
				return newTasks;
			});
		},
		[onTasksChange]
	);

	const onDeleteSuccess = useCallback(
		(task) => {
			setCurrentTasks((tasks) => {
				const newTasks = tasks.filter((_) => _.orderingId !== task.orderingId);
				onTasksChange && onTasksChange(newTasks);
				return newTasks;
			});
		},
		[onTasksChange]
	);

	const addNewGroup = useCallback(({ groupTitle }) => {
		setCurrentTasks((localTasks) => [
			...localTasks,
			...[
				{
					orderingId: getNewOrderingId(localTasks),
					groupTitle,
				},
			],
		]);
		onGroupTitleCancel();
	}, []);

	const onEditTask = useCallback(
		(task) => onTaskEdit(task).then(onSuccess),
		[onSuccess, onTaskEdit]
	);

	const onDeleteTask = useCallback(
		(task) => onTaskDelete(task).then(onDeleteSuccess),
		[onDeleteSuccess, onTaskDelete]
	);

	const groupExists = useMemo(() => groupedTasks.some((_) => !!_.groupTitle), [groupedTasks]);

	return (
		<>
			<GroupedTaskDisplay
				groupedTasks={groupedTasks}
				onEditTask={onEditTask}
				onDeleteTask={onDeleteTask}
				onAddNewTask={onAddNewTask}
				isAllowedToEdit={isAllowedToEdit}
				changeTitleForExistingTasks={changeTitleForExistingTasks}
				timeZoneId={timeZoneId}
				notCheckable={notCheckable}
				onGroupDelete={deleteGroup}
				onShowAssociate={onShowAssociate}
				workOrder={workOrder}
				associationDisabled={associationDisabled}
				addGroupDisabled={addGroupDisabled}
				hideIndividualWalkthroughLink={hideIndividualWalkthroughLink}
			/>
			{isAllowedToEdit ? (
				<div className="padding-less-antd-button mb-4 flex flex-row gap-2 pl-1">
					{addGroupDisabled ? null : (
						<Button onClick={onEditGroupTitle} type="link">
							<PlusOutlined /> Add group
						</Button>
					)}
					{groupExists ? null : (
						<div className="">
							<Button onClick={onAddNewTask('')} type="link">
								<PlusOutlined /> Add task
							</Button>
						</div>
					)}
				</div>
			) : null}
			{groupTitleModalVisible ? (
				<AddOrEditGroupTitleModal
					onCancel={onGroupTitleCancel}
					onSubmit={addNewGroup}
					saving={groupTitleSaving}
				/>
			) : null}
			{woAssociateModalVisible && currentEditingTask ? (
				<WOTaskEntityAssociateModal
					workOrder={workOrder}
					task={currentEditingTask}
					onCancel={_hideAssociateModal}
					onSave={onEditTask}
				/>
			) : null}
			{pmTemplateAssociateModalVisible && currentEditingTask ? (
				<PMTemplateTaskEntityAssociateModal
					task={currentEditingTask}
					onCancel={_hideAssociateModal}
					onSave={onEditTask}
				/>
			) : null}
		</>
	);
};

export default OWTaskComponent;
